'use client';

import { MAX_ITEMS_PER_PAGE, RANGE_FILTER_SECTIONS, SLEEPS_SEARCH_PARAM_KEY } from '@/constants/inventory';
import { useCustomSearchParams } from '@/hooks/useCustomSearchParams';
import {
  convertFiltersToSearchParams,
  convertSortBy,
  getFiltersFromSearchParams,
  makeGetSectionOptions,
  revertSortBy,
} from '@/tools/inventory';
import {
  BaseFilters,
  BaseTagFilters,
  Filters,
  FilterSection,
  ISectionOption,
  RangeFilterValue,
  SortBy,
  TagFilters,
} from '@/types/inventory';
import { getFacetCode } from '@lamesarv-sdk/tools';
import { InventoryField, InventoryFieldMap } from '@lamesarv-sdk/types';
import { createContext, PropsWithChildren, useCallback, useMemo } from 'react';

const getSearchParamKey = (name: string): string => {
  return name
    .split(' ')
    .map((word, index) => (index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1)))
    .join('');
};

export interface NewInventoryFiltersContextData {
  baseFilters: BaseFilters;
  filters: Filters;
  toggleFilter: (section: FilterSection, value: string) => void;
  addFilterOptions: (section: FilterSection, values: string[]) => void;
  removeFilterOptions: (section: FilterSection, values: string[]) => void;
  setRangeFilter: (section: FilterSection, value: RangeFilterValue) => void;
  clearFilters: () => void;
  sortBy: SortBy;
  setSortBy: (sortBy: SortBy) => void;
  query: string;
  setQuery: (query: string) => void;
  perPage: number | null;
  setPerPage: (perPage: number) => void;
  page: number;
  setPage: (page: number) => void;
  baseTagFilters: BaseTagFilters;
  tagFilters: TagFilters;
  toggleTagFilter: (section: string, value: string) => void;
  getSectionOptions: (section: string) => ISectionOption[];
}

export const NewInventoryFiltersContext = createContext({} as NewInventoryFiltersContextData);

const FILTER_KEYS = new Set([
  ...Object.values(InventoryField).map((field) => InventoryFieldMap.get(field) || field),
  SLEEPS_SEARCH_PARAM_KEY,
]);

interface NewInventoryFiltersProviderProps {
  baseFilters: BaseFilters;
  modelsByMake: Record<string, string[]>;
  baseTagFilters: BaseTagFilters;
  equipmentFilters: string[];
}

export const NewInventoryFiltersProvider = ({
  baseFilters,
  children,
  modelsByMake,
  baseTagFilters,
  equipmentFilters,
}: PropsWithChildren<NewInventoryFiltersProviderProps>) => {
  const { searchParams, setRawParams, removeSearchParam, setSearchParam } = useCustomSearchParams();

  let page: number | null = parseInt(searchParams.get('page'));
  if (isNaN(page) || page < 1) {
    page = 1;
  }
  const setPage = useCallback(
    (newPage: number) => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (newPage > 1) {
        newSearchParams.set('page', String(newPage));
      } else {
        newSearchParams.delete('page');
      }
      setRawParams(newSearchParams.toString());
    },
    [searchParams],
  );

  let perPage: number | null = parseInt(searchParams.get('perPage'));
  if (isNaN(perPage) || perPage < 1) {
    perPage = null;
  }
  if (perPage > MAX_ITEMS_PER_PAGE) {
    perPage = MAX_ITEMS_PER_PAGE;
  }
  const setPerPage = useCallback(
    (newPerPage: number) => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (newPerPage && newPerPage > 0 && newPerPage <= MAX_ITEMS_PER_PAGE) {
        newSearchParams.set('perPage', String(newPerPage));
      } else {
        newSearchParams.delete('perPage');
      }
      setRawParams(newSearchParams.toString());
    },
    [searchParams],
  );

  const sortBy = revertSortBy(searchParams.get('sortBy'));
  const setSortBy = useCallback(
    (newSortBy: SortBy) => {
      if (sortBy === newSortBy) return;

      const newSearchParams = new URLSearchParams(searchParams);

      if (newSortBy === SortBy.RECOMMENDED) {
        newSearchParams.delete('sortBy');
      } else {
        newSearchParams.set('sortBy', convertSortBy(newSortBy));
      }

      newSearchParams.delete('page');

      setRawParams(newSearchParams.toString());
    },
    [searchParams, sortBy],
  );

  const query = searchParams.get('q') || '';
  const setQuery = useCallback(
    (newQuery: string) => {
      if (query === newQuery) return;

      const newSearchParams = new URLSearchParams(searchParams);

      if (newQuery) {
        newSearchParams.set('q', newQuery);
      } else {
        newSearchParams.delete('q');
      }

      newSearchParams.delete('page');

      setRawParams(newSearchParams.toString());
    },
    [searchParams, query],
  );

  const filters = useMemo(() => getFiltersFromSearchParams(searchParams, baseFilters), [searchParams, baseFilters]);
  const setFilters = useCallback(
    (newFiltersDelta: Filters | ((prevFilters: Filters) => Filters)) => {
      const newFilters = typeof newFiltersDelta === 'function' ? newFiltersDelta(filters) : newFiltersDelta;
      const newSearchParams = convertFiltersToSearchParams(newFilters);

      searchParams.forEach((value, key) => {
        if (!FILTER_KEYS.has(key)) {
          newSearchParams.append(key, value);
        }
      });

      newSearchParams.delete('page');

      setRawParams(newSearchParams.toString());
    },
    [searchParams, filters],
  );

  const toggleFilter = useCallback(
    (section: FilterSection, value: string) => {
      setFilters((prevFilters) => {
        const currentValues = prevFilters[section] || [];

        if (RANGE_FILTER_SECTIONS.has(section) || !(currentValues instanceof Array)) return prevFilters;

        const newValues = currentValues.includes(value)
          ? currentValues.filter((v) => v !== value)
          : [...currentValues, value];

        if (newValues.length === 0) {
          const newFilters = { ...prevFilters };
          delete newFilters[section];
          return newFilters;
        }

        return {
          ...prevFilters,
          [section]: newValues,
        };
      });
    },
    [setFilters],
  );

  const addFilterOptions = useCallback(
    (section: FilterSection, values: string[]) => {
      setFilters((prevFilters) => {
        if (RANGE_FILTER_SECTIONS.has(section)) return prevFilters;

        const currentValues = prevFilters[section] || [];

        if (!(currentValues instanceof Array)) return prevFilters;

        const newValues = Array.from(new Set([...currentValues, ...values]));

        return {
          ...prevFilters,
          [section]: newValues,
        };
      });
    },
    [setFilters],
  );

  const removeFilterOptions = useCallback(
    (section: FilterSection, values: string[]) => {
      setFilters((prevFilters) => {
        if (RANGE_FILTER_SECTIONS.has(section)) return prevFilters;

        const currentValues = prevFilters[section] || [];

        if (!(currentValues instanceof Array)) return prevFilters;

        const newValues = currentValues.filter((v) => !values.includes(v));

        if (newValues.length === 0) {
          const newFilters = { ...prevFilters };
          delete newFilters[section];
          return newFilters;
        }

        return {
          ...prevFilters,
          [section]: newValues,
        };
      });
    },
    [setFilters],
  );

  const setRangeFilter = useCallback(
    (section: FilterSection, value?: RangeFilterValue | null) => {
      if (!RANGE_FILTER_SECTIONS.has(section)) return;

      setFilters((prevFilters) => {
        if (!value) {
          const newFilters = { ...prevFilters };
          delete newFilters[section];
          return newFilters;
        }

        return {
          ...prevFilters,
          [section]: value,
        };
      });
    },
    [setFilters],
  );

  const { tagBySearchKey, tagFilterFacetRecordByKey } = useMemo(() => {
    const tagFilterFacetRecordByKey = new Map<string, Map<string, string>>();
    const tagBySearchKey = new Map<string, string>();
    for (const item of baseTagFilters) {
      const key = getSearchParamKey(item.name);
      tagBySearchKey.set(key, item.name);

      const facetMap = new Map<string, string>();
      for (const value of item.values) {
        facetMap.set(getFacetCode(value), value);
      }

      tagFilterFacetRecordByKey.set(key, facetMap);
    }
    return { tagFilterFacetRecordByKey, tagBySearchKey };
  }, [baseTagFilters]);

  const clearFilters = useCallback(() => {
    const newSearchParams = new URLSearchParams();

    searchParams.forEach((value, key) => {
      if (!FILTER_KEYS.has(key) && key !== 'sortBy' && key !== 'q' && key !== 'page' && !tagBySearchKey.has(key)) {
        newSearchParams.append(key, value);
      }
    });

    setRawParams(newSearchParams.toString());
  }, [searchParams]);

  const tagFilters = useMemo((): TagFilters => {
    const tagFilters: TagFilters = {};

    searchParams.forEach((value, key) => {
      if (!tagFilterFacetRecordByKey.has(key)) return;

      const facetValues = value
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);

      if (facetValues.length === 0) return;

      const tagName = tagBySearchKey.get(key);

      for (const facetValue of facetValues) {
        const tagValue = tagFilterFacetRecordByKey.get(key)?.get(facetValue);

        if (!tagValue) {
          continue;
        }

        if (!tagFilters[tagName]) {
          tagFilters[tagName] = [];
        }

        tagFilters[tagName].push(tagValue);
      }
    });

    return tagFilters;
  }, [searchParams, tagFilterFacetRecordByKey, tagBySearchKey]);

  const toggleTagFilter = useCallback(
    (section: string, value: string) => {
      const prevValue = tagFilters[section] || [];

      const newValues = prevValue.includes(value) ? prevValue.filter((v) => v !== value) : [...prevValue, value];

      const sectionSearchKey = getSearchParamKey(section);

      if (newValues.length === 0) {
        removeSearchParam(sectionSearchKey);
        return;
      }

      setSearchParam(sectionSearchKey, newValues.map(getFacetCode).join(','));
    },
    [tagFilters],
  );

  const getSectionOptions = useMemo(
    () => makeGetSectionOptions({ baseFilters, equipmentFilters, filters, modelsByMake }),
    [baseFilters, equipmentFilters, filters, modelsByMake],
  );

  const contextData = useMemo(
    (): NewInventoryFiltersContextData => ({
      baseFilters,
      filters,
      toggleFilter,
      setRangeFilter,
      clearFilters,
      sortBy,
      setSortBy,
      query,
      setQuery,
      perPage,
      setPerPage,
      page,
      setPage,
      baseTagFilters,
      tagFilters,
      toggleTagFilter,
      getSectionOptions,
      addFilterOptions,
      removeFilterOptions,
    }),
    [
      baseFilters,
      filters,
      toggleFilter,
      setRangeFilter,
      clearFilters,
      sortBy,
      setSortBy,
      query,
      setQuery,
      perPage,
      setPerPage,
      page,
      setPage,
      baseTagFilters,
      tagFilters,
      toggleTagFilter,
      getSectionOptions,
      addFilterOptions,
      removeFilterOptions,
    ],
  );

  return <NewInventoryFiltersContext.Provider value={contextData}>{children}</NewInventoryFiltersContext.Provider>;
};
